/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

/* eslint-disable no-nested-ternary */
import { useDispatch, useSelector } from 'react-redux';
import {
  selectTrendSymmetricComparisonToggle,
  selectTrendTimeDateComparison,
  selectTrendTimeAlignmentToggle,
  selectTrendSegmentChartBreakdown,
  selectIsSegmentDetailed,
  selectTrendSegmentFocusOn,
  selectIsForecast,
  selectTrendStartDate,
  selectTrendEndDate,
  selectIsCumulative,
  selectMovingAverage,
  selectTrendTimeWeeklyView,
  selectIsMovingAverage,
  selectIsPercentageChange,
  selectIsPrimary,
  selectIsTrendDeAggregate,
  selectSegmentGroup1,
  selectSegmentGroup2,
  selectSegmentSetAName,
  selectSegmentSetBName,
  selectTrendTimeStartDate,
  selectTrendTimeEndDate,
  selectTrendHotelFocusOn,
  selectTrendHotelNormalizedView,
  selectTrendGranularity,
  selectEnableSecondaryHotelGroups,
} from 'modules/dashboard/components/tab-container/trend-tabs/selectors';
import {
  selectHotelName,
  selectLastPartialBookingDate,
  selectLocalCurrency,
  selectPartialHours,
  selectPmsSync,
  selectResetBookingDate,
  selectTrendBookingDate,
  selectVisualFilters,
} from 'modules/dashboard/selectors';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Box } from '@mui/system';
import MuiTabular from 'modules/common/components/mui-tabular';
import { IconButton, Typography } from '@mui/material';
import useAbortableRequest from 'modules/common/hooks/use-abortable-request';
import { isRequestCanceled } from 'modules/common/utils/request';
import { API, EXPORT_OPTIONS } from 'modules/dashboard/constants';
import {
  selectAuthUser,
  selectOrganizationId,
  selectSessionId,
} from 'modules/common/auth/selectors';
import { EXPORT_BUTTON_STYLE } from 'modules/common/components/mui-tabular/style';
import DownloadIcon from '@mui/icons-material/Download';
import { useDebouncedCallback } from 'use-debounce';
import { AssistantIcon } from 'modules/common/components';
import {
  BREAKDOWN_LIST_GETTER_COLUMNS,
  TREND_HOTEL_CUMULATIVE_FORECAST_TABLE_COLUMNS,
  TREND_HOTEL_CUMULATIVE_TABLE_COLUMNS,
  TREND_HOTEL_MOVING_AVERAGE_TABLE_COLUMNS,
  TREND_HOTEL_PERCENTAGE_TABLE_COLUMNS,
  TREND_HOTEL_WEEKLY_FORECAST_TABLE_COLUMNS,
  TREND_HOTEL_TABLE_COLUMNS,
  TREND_HOTEL_WEEKLY_TABLE_COLUMNS,
} from '../../../constants';
import {
  buildSegmentSchema,
  buildTrendFilters,
  buildTrendHeaderData,
  getColumns,
  handleCeruleanLogging,
} from '../../../function';
import TabularFilterHeader from './filter-header';
import { selectTrendData, selectTrendDataResponseId } from '../../../selectors';
import { trendTabularActions } from '../../../slice';

/**
 * Trend Hotel tabular visual report with filters
 */

const MuiTrendHotelTabularWidget = ({ setIsTimeLoaded, isLoaded }) => {
  const bookingDate = useSelector(selectTrendBookingDate);
  const currencyType = useSelector(selectLocalCurrency);
  const hotels = useSelector(selectHotelName);
  const hotelFocusOn = useSelector(selectTrendHotelFocusOn);
  const targetStartDate = useSelector(selectTrendStartDate);
  const targetEndDate = useSelector(selectTrendEndDate);
  const alignmentToggle = useSelector(selectTrendTimeAlignmentToggle);
  const symmetricComparisonToggle = useSelector(selectTrendSymmetricComparisonToggle);
  const dateComparison = useSelector(selectTrendTimeDateComparison);
  const pms = useSelector(selectPmsSync);
  const normalization = useSelector(selectTrendHotelNormalizedView);
  const visualFilters = useSelector(selectVisualFilters);
  const chartBreakdown = useSelector(selectTrendSegmentChartBreakdown);
  const isDetailed = useSelector(selectIsSegmentDetailed);
  const focusOn = useSelector(selectTrendSegmentFocusOn);
  const isForecast = useSelector(selectIsForecast);
  const isCumulative = useSelector(selectIsCumulative);
  const organizationId = useSelector(selectOrganizationId);
  const movingAverage = useSelector(selectMovingAverage);
  const isMovingAverage = useSelector(selectIsMovingAverage);
  const isWeeklyView = useSelector(selectTrendTimeWeeklyView);
  const isPercentageChange = useSelector(selectIsPercentageChange);
  const isBreakdown = useSelector(selectIsPrimary);
  const isTrendDeAggregate = useSelector(selectIsTrendDeAggregate);
  const endDate = useSelector(selectTrendTimeEndDate);
  const startDate = useSelector(selectTrendTimeStartDate);
  const segmentGroupA = useSelector(selectSegmentGroup1);
  const segmentGroupB = useSelector(selectSegmentGroup2);
  const segmentGroupAName = useSelector(selectSegmentSetAName);
  const segmentGroupBName = useSelector(selectSegmentSetBName);
  const user = useSelector(selectAuthUser);
  const sessionId = useSelector(selectSessionId);
  const trendData = useSelector(selectTrendData);
  const trendDataResponseId = useSelector(selectTrendDataResponseId);
  const resetBookingDate = useSelector(selectResetBookingDate);
  const trendGranularity = useSelector(selectTrendGranularity);
  const partialHours = useSelector(selectPartialHours);
  const lastBookingDate = useSelector(selectLastPartialBookingDate);
  const enableSecondaryHotelGroups = useSelector(selectEnableSecondaryHotelGroups);
  //
  const dispatch = useDispatch();
  const abortableRequest = useAbortableRequest();
  //
  const tableRef = useRef();
  //
  const [exportType, setExportType] = useState(EXPORT_OPTIONS.DATA_EXPORT);
  //
  const [isHotelComparison] = useState(true);
  //
  const [isProcessing, setIsProcessing] = useState(false);
  //
  const [exportData, setExportData] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  // Debounce the request to avoid excessive calls
  const fetchData = useDebouncedCallback(async () => {
    // Build the filters for the API request
    const filters = buildTrendFilters({
      bookingDate,
      targetStartDate,
      targetEndDate,
      alignmentToggle,
      symmetricComparisonToggle,
      dateComparison,
      currencyType,
      hotels,
      hotelFocusOn,
      pms,
      chartBreakdown,
      visualFilters,
      isDetailed,
      focusOn,
      isForecast,
      movingAverage,
      isMovingAverage,
      startDate,
      endDate,
      segmentGroupA,
      segmentGroupB,
      segmentGroupAName,
      segmentGroupBName,
      resetBookingDate,
      normalization,
      trendGranularity,
      partialHours,
      lastBookingDate,
    });
    //
    const columns = getColumns({
      isPercentageChange,
      isMovingAverage,
      isTrendDeAggregate,
      isCumulative,
      isWeeklyView,
      isForecast,
      isHotelComparison,
    });
    //
    let start;
    try {
      setIsTimeLoaded(false);
      // Log the request time for performance monitoring
      handleCeruleanLogging({
        message: `Trend Hotel Tabular Filters: ${JSON.stringify(filters)}`,
        sessionId,
        user,
        eventType: 'Cerulean Filters',
      });
      handleCeruleanLogging({
        message: `Trend Tabular Request start: ${new Date()}`,
        sessionId,
        user,
        eventType: 'Cerulean Loading',
      });
      start = performance.now();
      //
      const breakdownListData = await abortableRequest(API.GET_JS_VISUAL_DATA, {
        organizationId,
        columns: BREAKDOWN_LIST_GETTER_COLUMNS,
        filters,
      });

      const breakdownList = breakdownListData?.map((item) => item?.channel_mapped);
      if (filters?.breakDownList?.conditions) {
        const isTotalInclude = filters.breakDownList.conditions.values
          .map((v) => v.replace(/\u200B/g, '').trim())
          .includes('Total');
        filters.breakDownList.conditions.values = [
          ...breakdownList,
          ...(isTotalInclude ? ['Total'] : []),
        ];
      }
      //
      setExportData({
        columns,
        filters,
        title: 'mui-tabular-trend-hotel',
      });
      dispatch(
        trendTabularActions.getTrendData({
          organizationId,
          columns,
          // columns: [{ table: 'measures', columns: 'trend_tabular_hotel_daily' }],
          filters,
        })
      );
    } catch (error) {
      if (isRequestCanceled(error)) return;
      handleCeruleanLogging({
        message: `Error fetching trend hotel tabular data: ${error}`,
        sessionId,
        user,
        eventType: 'Cerulean Error',
      });
    } finally {
      // Log the time taken for the request
      const end = performance.now();
      handleCeruleanLogging({
        message: `Trend Tabular Request finished: ${new Date()}`,
        sessionId,
        user,
        eventType: 'Cerulean Loaded',
      });
      handleCeruleanLogging({
        message: `Trend Tabular Request time: ${((end - start) / 1000).toFixed(2)} seconds`,
        sessionId,
        user,
        eventType: 'Cerulean Request Time',
      });
    }
  }, 500);
  //
  useEffect(() => {
    // loaded when trend data is available or a request is completed, also after an error
    if (trendData || trendDataResponseId > 0) {
      setIsTimeLoaded(true);
    }
  }, [trendData, trendDataResponseId]);
  //
  useEffect(() => {
    fetchData();
  }, [
    bookingDate,
    currencyType,
    hotels,
    hotelFocusOn,
    alignmentToggle,
    symmetricComparisonToggle,
    dateComparison,
    setIsTimeLoaded,
    targetStartDate,
    targetEndDate,
    pms,
    normalization,
    focusOn,
    isCumulative,
    isForecast,
    movingAverage,
    isWeeklyView,
    isMovingAverage,
    isPercentageChange,
    isHotelComparison,
    startDate,
    endDate,
    isBreakdown,
    isTrendDeAggregate,
    segmentGroupA,
    segmentGroupB,
    segmentGroupAName,
    segmentGroupBName,
    trendGranularity,
    partialHours,
    lastBookingDate,
  ]);
  //
  const headerData = useMemo(
    () =>
      buildTrendHeaderData({
        hotelName: hotels?.label,
        hotelFocusOn: hotelFocusOn?.label,
        isComparisonHotelSet: Boolean(hotelFocusOn?.hotels),
        bookingDate,
        targetStartDate,
        targetEndDate,
        alignmentToggle,
        symmetricComparisonToggle,
        currencyType,
        aligned: alignmentToggle,
        pmsSync: pms,
        normalization,
        dateComparison,
        startDate,
        endDate,
        isBreakdown,
        isCumulative,
        isPercentageChange,
        isWeeklyView,
        isMovingAverage,
        isHotelComparison,
        resetBookingDate,
        partialHours,
        lastBookingDate,
      }),
    [
      hotels,
      hotelFocusOn,
      bookingDate,
      targetEndDate,
      targetStartDate,
      alignmentToggle,
      startDate,
      endDate,
      symmetricComparisonToggle,
      currencyType,
      pms,
      normalization,
      dateComparison,
      isBreakdown,
      isCumulative,
      isPercentageChange,
      isWeeklyView,
      isMovingAverage,
      resetBookingDate,
      trendGranularity,
      partialHours,
      lastBookingDate,
    ]
  );
  const tableColumns = () => {
    const rowKeys = Object.keys(trendData?.[0]);
    const granularity = rowKeys?.find((val) => ['day_name', 'date_name', 'StayKey']?.includes(val));

    if (isBreakdown && !isTrendDeAggregate) {
      return buildSegmentSchema(trendData?.[0], isWeeklyView);
    }
    if (isCumulative && isForecast) {
      return TREND_HOTEL_CUMULATIVE_FORECAST_TABLE_COLUMNS(granularity);
    }
    if (isWeeklyView && isForecast) {
      return TREND_HOTEL_WEEKLY_FORECAST_TABLE_COLUMNS;
    }
    if (isCumulative && !isWeeklyView) {
      return TREND_HOTEL_CUMULATIVE_TABLE_COLUMNS(granularity);
    }
    if (isMovingAverage && isPercentageChange) {
      return TREND_HOTEL_PERCENTAGE_TABLE_COLUMNS;
    }
    if (isMovingAverage) {
      return TREND_HOTEL_MOVING_AVERAGE_TABLE_COLUMNS;
    }
    if (isWeeklyView) {
      return TREND_HOTEL_WEEKLY_TABLE_COLUMNS;
    }
    return TREND_HOTEL_TABLE_COLUMNS;
  };
  //
  return (
    <Box px={10}>
      {!trendData ? (
        <Typography fontWeight={500}>Loading Data...</Typography>
      ) : (
        <Box>
          <Box display="flex" justifyContent="end">
            <IconButton
              size="medium"
              onClick={() => setIsProcessing(true)}
              sx={EXPORT_BUTTON_STYLE}
            >
              <Typography
                sx={{
                  mr: 1,
                }}
              >
                Export Data
              </Typography>
              <DownloadIcon />
            </IconButton>
          </Box>
          <MuiTabular
            ref={tableRef}
            columns={tableColumns()}
            isProcessing={isProcessing}
            isLoadingData={!isLoaded}
            setIsProcessing={setIsProcessing}
            isAggregate={!(isMovingAverage || isPercentageChange)}
            headerComponent={
              <TabularFilterHeader
                title={headerData?.title}
                hotelName={headerData?.hotelName}
                isHotelSet={Boolean(hotels?.hotels)}
                comparisonHotelName={headerData?.comparisonHotelName}
                isComparisonHotelSet={headerData?.isComparisonHotelSet}
                sections={headerData?.sections}
                chunkSize={1}
                interpreterFragment={
                  <AssistantIcon
                    exportData={exportData}
                    visualName={headerData?.title}
                    isProcessing={isExporting}
                    setIsProcessing={setIsExporting}
                    disabled={isProcessing || !isLoaded || isExporting}
                    isCerulean
                    enableSecondaryHotelGroups={enableSecondaryHotelGroups}
                    getExcelExportData={tableRef?.current?.getExcelData}
                    exportType={exportType}
                    setExportType={setExportType}
                  />
                }
              />
            }
            data={trendData}
            headerFiltersInfo={headerData}
            customBackgroundStyle={{ paddingLeft: 3, maxWidth: { md: '72vw', lg: '76vw' } }}
            customHeaderStyle={isBreakdown ? { lineHeight: 1.2 } : {}}
            fileName={
              isMovingAverage
                ? isPercentageChange
                  ? 'TVH_Percentage'
                  : 'CMATH_Daily'
                : isCumulative
                ? 'TVH_Cumulative'
                : 'TVH_Daily'
            }
            primaryTextColor={isPercentageChange ? '#000' : undefined}
            secondaryTextColor={isPercentageChange ? '#000' : undefined}
            removeEmptyColumns={isBreakdown}
          />
        </Box>
      )}
    </Box>
  );
};
//
export default MuiTrendHotelTabularWidget;
